// Centralized server-time store: one offset fetch + one global ticker.

import { auth } from "../firebase";

const SEC_MS = 1000;
const CACHE_KEY = "serverOffset";
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

let serverTime = null;          // current server time (ms)
let subs = new Set();           // subscribers
let offset = null;              // server - client offset (ms)
let tickerId = null;            // global setInterval id
let fetchingOffset = null;      // in-flight fetch promise

function notify() {
  subs.forEach((cb) => cb(serverTime));
}

export function getServerTime() {
  return serverTime;
}

export function setServerTime(ms) {
  serverTime = ms;
  notify();
}

export function subscribeServerTime(cb) {
  subs.add(cb);
  // push current value immediately if we have one
  if (serverTime != null) cb(serverTime);
  return () => subs.delete(cb);
}

async function resolveUser() {
  if (auth.currentUser) return auth.currentUser;
  return new Promise((resolve) => {
    const unsub = auth.onAuthStateChanged((u) => {
      unsub();
      resolve(u);
    });
  });
}

async function fetchOffset() {
  // in-memory
  if (offset != null) return offset;

  // cache
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { off, ts } = JSON.parse(cached);
      if (Date.now() - ts < CACHE_TTL_MS) {
        offset = off;
        return offset;
      }
    }
  } catch { /* ignore cache errors */ }

  // de-dupe parallel fetches
  if (fetchingOffset) return fetchingOffset;

  fetchingOffset = (async () => {
    const user = await resolveUser();
    if (!user) throw new Error("No authenticated user");

    const token = await user.getIdToken(true);
    const res = await fetch("https://gettime-5dhfwyjvzq-uc.a.run.app", {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const { timestamp } = await res.json();
    const tsNum = typeof timestamp === "string" ? Number(timestamp) : timestamp;
    const serverMs = String(tsNum).length <= 10 ? tsNum * SEC_MS : tsNum;

    offset = serverMs - Date.now();

    try {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ off: offset, ts: Date.now() })
      );
    } catch { /* ignore cache errors */ }

    return offset;
  })();

  try {
    await fetchingOffset;
    return offset;
  } finally {
    fetchingOffset = null;
  }
}

// Public: start/stop the single global ticker.
export async function startServerClock() {
  if (tickerId) return; // already running
  const off = await fetchOffset();
  setServerTime(Date.now() + off);
  tickerId = setInterval(() => {
    setServerTime(Date.now() + off);
  }, SEC_MS);
}

export function stopServerClock() {
  if (tickerId) {
    clearInterval(tickerId);
    tickerId = null;
  }
}

// Optional: export the offset getter if you still need it elsewhere.
export async function getServerOffset() {
  return fetchOffset();
}
